// HALVERE product card picker (snippets/halvere-card-options.liquid).
// Keeps the hidden variant id of Atelier's product form in step with the chosen
// option values. Atelier's product-form-component does the add itself and refuses
// to submit while the add button is disabled, so an incomplete or sold-out choice
// can never reach the cart.

class HalvereCardPicker extends HTMLElement {
  connectedCallback() {
    if (this.variants) return;

    const data = this.querySelector('script[data-halvere-variants]');
    /** @type {{id: number, available: boolean, options: string[], media: number | null}[]} */
    this.variants = data ? JSON.parse(data.textContent || '[]') : [];
    this.fieldsets = [...this.querySelectorAll('fieldset[data-option-index]')];
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-halvere-variant-input]'));
    this.status = this.querySelector('[data-halvere-status]');

    this.addEventListener('change', this.#onChange);
    this.#update(false);
  }

  /** @returns {(string | null)[]} The checked value per option, null when none. */
  get selection() {
    return this.fieldsets.map((fieldset) => {
      const checked = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'));
      return checked ? checked.value : null;
    });
  }

  #onChange = (/** @type {Event} */ event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;
    const colourChanged = event.target.closest('fieldset') === this.fieldsets[0];
    this.#update(colourChanged);
  };

  /** @param {boolean} showImage */
  #update(showImage) {
    const selection = this.selection;
    const complete = selection.every((value) => value !== null);
    const variant = complete
      ? this.variants.find((candidate) => candidate.options.every((value, index) => value === selection[index]))
      : undefined;

    this.#markCombinations(selection);

    const available = Boolean(variant && variant.available);
    if (this.input) this.input.value = available && variant ? String(variant.id) : '';

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('button[name="add"]'));
    if (button) {
      button.disabled = !available;
      const label = !complete
        ? this.dataset.textChoose
        : available
          ? this.dataset.textAdd
          : this.dataset.textSoldOut;
      this.#setButtonText(button, label || '');
    }

    if (this.status) {
      this.status.textContent = complete && !available ? this.dataset.textSoldOut || '' : '';
    }

    // The colour (first option) decides the image; use any variant of that colour.
    const imageVariant =
      variant || this.variants.find((candidate) => candidate.options[0] === selection[0] && candidate.media);
    if (imageVariant) this.#showImage(imageVariant, showImage);
    if (variant) this.#showPrice(variant.id);
  }

  /**
   * Marks values that are sold out together with the other chosen values.
   * Values sold out in every combination are already disabled in the markup.
   * @param {(string | null)[]} selection
   */
  #markCombinations(selection) {
    this.fieldsets.forEach((fieldset, optionIndex) => {
      for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (fieldset.querySelectorAll('input'))) {
        const possible = this.variants.some(
          (variant) =>
            variant.available &&
            variant.options[optionIndex] === input.value &&
            variant.options.every(
              (value, index) => index === optionIndex || selection[index] === null || selection[index] === value
            )
        );
        input.closest('label')?.classList.toggle('halvere-card-picker__value--unavailable', !input.disabled && !possible);
      }
    });
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} text
   */
  #setButtonText(button, text) {
    const node = [...button.childNodes].find(
      (child) => child.nodeType === Node.TEXT_NODE && child.textContent && child.textContent.trim()
    );
    if (node) node.textContent = text;
  }

  /**
   * Shows the variant's image in the card gallery, the way Atelier does for card swatches.
   * @param {{media: number | null}} variant
   * @param {boolean} select
   */
  #showImage(variant, select) {
    if (!variant.media || !select) return;
    const card = this.closest('product-card');
    const slideshow = /** @type {any} */ (card?.querySelector('slideshow-component'));
    if (!slideshow) return;

    const id = String(variant.media);
    const slides = [...slideshow.querySelectorAll('slideshow-slide')];
    if (!slides.some((slide) => slide.getAttribute('slide-id') === id)) return;

    for (const slide of slides) {
      if (slide.getAttribute('variant-image') == null) continue;
      slide.hidden = slide.getAttribute('slide-id') !== id;
    }
    if (typeof slideshow.select === 'function') slideshow.select({ id }, undefined, { animate: false });
  }

  /** @param {number} variantId */
  #showPrice(variantId) {
    const template = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector(`template[data-halvere-price="${variantId}"]`)
    );
    const container = this.closest('product-card')?.querySelector("product-price [ref='priceContainer']");
    if (!template || !container) return;
    container.replaceChildren(template.content.cloneNode(true));
  }
}

if (!customElements.get('halvere-card-picker')) {
  customElements.define('halvere-card-picker', HalvereCardPicker);
}
